import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import {
  DynamoDBDocumentClient,
  PutCommand,
  GetCommand,
  QueryCommand,
  UpdateCommand,
  ScanCommand,
} from "@aws-sdk/lib-dynamodb";

const REGION = "us-east-1";

let docClient: DynamoDBDocumentClient | null = null;

export function configureDb(credentials: {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}) {
  const raw = new DynamoDBClient({ region: REGION, credentials });
  docClient = DynamoDBDocumentClient.from(raw, {
    marshallOptions: { removeUndefinedValues: true },
  });
}

export function getDocClient(): DynamoDBDocumentClient {
  if (!docClient) {
    const raw = new DynamoDBClient({ region: REGION });
    docClient = DynamoDBDocumentClient.from(raw, {
      marshallOptions: { removeUndefinedValues: true },
    });
  }
  return docClient;
}

// Table names — deployed via CDK
export const Tables = {
  COMMITS: "pos-commits",
  VERSIONS: "pos-versions",
  FEATURES: "pos-feature-requests",
  BUGS: "pos-bug-reports",
  DEPLOYMENTS: "pos-deployments",
  USERS: "pos-portal-users",
} as const;

// --- Generic helpers ---

export async function putItem(table: string, item: Record<string, any>) {
  await getDocClient().send(new PutCommand({ TableName: table, Item: item }));
}

export async function getItem(table: string, key: Record<string, any>) {
  const result = await getDocClient().send(new GetCommand({ TableName: table, Key: key }));
  return result.Item;
}

export async function queryByPartition(
  table: string,
  keyName: string,
  keyValue: string,
  indexName?: string
) {
  const result = await getDocClient().send(
    new QueryCommand({
      TableName: table,
      IndexName: indexName,
      KeyConditionExpression: `#pk = :pk`,
      ExpressionAttributeNames: { "#pk": keyName },
      ExpressionAttributeValues: { ":pk": keyValue },
      ScanIndexForward: false, // newest first
    })
  );
  return result.Items || [];
}

export async function scanAll(table: string) {
  const items: Record<string, any>[] = [];
  let lastKey: Record<string, any> | undefined;

  do {
    const result = await getDocClient().send(
      new ScanCommand({ TableName: table, ExclusiveStartKey: lastKey })
    );
    items.push(...(result.Items || []));
    lastKey = result.LastEvaluatedKey;
  } while (lastKey);

  return items;
}

export async function updateItem(
  table: string,
  key: Record<string, any>,
  updates: Record<string, any>
) {
  const expressions: string[] = [];
  const names: Record<string, string> = {};
  const values: Record<string, any> = {};

  Object.entries(updates).forEach(([field, value], i) => {
    const nameKey = `#f${i}`;
    const valueKey = `:v${i}`;
    expressions.push(`${nameKey} = ${valueKey}`);
    names[nameKey] = field;
    values[valueKey] = value;
  });

  await getDocClient().send(
    new UpdateCommand({
      TableName: table,
      Key: key,
      UpdateExpression: `SET ${expressions.join(", ")}`,
      ExpressionAttributeNames: names,
      ExpressionAttributeValues: values,
    })
  );
}
